import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useReactFlow } from "reactflow";
import { useGetAllFacilitiesForSearch, useGetFacilityWholeList } from "../../../../../../api/cultivations";
import { DrawingContext } from "../../../../../../components/drawing-tool/DrawingTool";
import { isNodeLabelExists } from "../../../../../../components/drawing-tool/utils";
import { NEW_FACILITY_KEY } from "../../../../../../config/constants";
import { FacilityType } from "../../../../../../config/enums/FacilityType.enum";
import { NodeLabelExistError } from "../../../../../../config/enums/NodeLabelError.enum";
import { errorView, successMessage } from "../../../../../../helpers/ErrorHandler";
import { hasValidationErrors } from "../../../../../../utils/utils";
import { DrawingForIngredientContext } from "../../../../../supplier_drawing_tool/DrawingToolForIngredient";
import { getExternalStorageApiHooks } from "../externalStorageApi.config";
import {
  ExternalStorageFormValues,
  ExternalStorageToolVariant,
  SaveDocumentDataHandle
} from "../externalStorage.types";
import {
  buildDefaultFormValues,
  buildExternalStorageNodeLabel,
  buildExternalStoragePayload,
  isNewFacilitySelection,
  isSpecificDataType,
  mapDropdownFacilities,
  mapFormValuesToLoadedData,
  mapSearchFacilities
} from "../externalStorage.utils";
import { getExternalStorageVariantConfig } from "../externalStorageVariant.config";

interface UseExternalStorageBaseScreenOptions {
  variant?: ExternalStorageToolVariant;
}

export const useExternalStorageBaseScreen = ({
  variant = "mapping"
}: UseExternalStorageBaseScreenOptions) => {
  const mappingContext = useContext(DrawingContext);
  const supplierContext = useContext(DrawingForIngredientContext);
  const drawingContext = variant === "supplier" ? supplierContext : mappingContext;
  const variantConfig = getExternalStorageVariantConfig(variant);
  const apiHooks = getExternalStorageApiHooks(variant);

  const {
    nodeItemId,
    lockStatus = false,
    setNodeItem,
    setChartNodes,
    saveDrawing,
    chartEdges,
    chartNodes,
    ownerId,
    sourceId
  } = drawingContext as typeof mappingContext;

  const supplierId = variant === "supplier" ? supplierContext.supplierId : undefined;
  const supplierIngredientId =
    variant === "supplier" ? supplierContext.supplierIngredientId : undefined;

  const { deleteElements, getEdges, getNode } = useReactFlow();

  const resolvedNodeId = useMemo(
    () => (typeof nodeItemId === "string" ? nodeItemId : nodeItemId?.id || ""),
    [nodeItemId]
  );

  const nodeItem = useMemo(
    () => (resolvedNodeId ? getNode(resolvedNodeId) : undefined),
    [getNode, resolvedNodeId]
  );

  const notesRef = useRef<SaveDocumentDataHandle | null>(null);
  const newlyCreatedFacilityRef = useRef<string | null>(null);
  const loadedStorageIdRef = useRef<string | null>(null);
  const loadedOriginalIdRef = useRef<string | null>(null);

  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const [metricsExpanded, setMetricsExpanded] = useState(true);
  const [notesExpanded, setNotesExpanded] = useState(true);
  const [showFacilityRegister, setShowFacilityRegister] = useState(false);
  const [visibleFacilityManager, setVisibleFacilityManager] = useState(false);
  const [visibleFacilityModal, setVisibleFacilityModal] = useState(false);
  const [savedExternalStorageId, setSavedExternalStorageId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [loadedData, setLoadedData] = useState<any>(null);
  const [originalData, setOriginalData] = useState<any>(null);

  const externalStorageId =
    nodeItem?.data?.reference?.externalStorageId || savedExternalStorageId || null;

  const simulateOriginalId =
    variant === "simulate" ? loadedData?.originalId ?? null : null;

  useEffect(() => {
    if (nodeItem?.data?.reference?.externalStorageId) {
      setSavedExternalStorageId(null);
    }
  }, [nodeItem?.data?.reference?.externalStorageId]);

  const connectedEdges = getEdges().filter(
    edge => edge.source === resolvedNodeId || edge.target === resolvedNodeId
  );

  const dropdownFacilitiesQuery = useGetFacilityWholeList(FacilityType.STORAGE);
  const searchFacilitiesQuery = useGetAllFacilitiesForSearch(FacilityType.STORAGE);

  const facilities = useMemo(
    () =>
      mapDropdownFacilities(
        dropdownFacilitiesQuery.data || [],
        variantConfig.supportsNewFacility
      ),
    [dropdownFacilitiesQuery.data, variantConfig.supportsNewFacility]
  );

  const searchFacilities = useMemo(
    () => mapSearchFacilities(searchFacilitiesQuery.availableFacilities || []),
    [searchFacilitiesQuery.availableFacilities]
  );

  const defaultFacilityId = useMemo(() => {
    const list = dropdownFacilitiesQuery.data || [];
    return list.find((item: { value?: string }) => item.value !== NEW_FACILITY_KEY)?.value;
  }, [dropdownFacilitiesQuery.data]);

  const { refetch: refetchStorage } = apiHooks.useGetById(externalStorageId);
  const { refetch: refetchOriginal, isLoading: isOriginalLoading } =
    apiHooks.useGetOriginalById(simulateOriginalId);

  const { mutate: createExternalStorage, isLoading: createLoading } = apiHooks.useCreate();
  const { mutate: updateExternalStorage, isLoading: updateLoading } = apiHooks.useUpdate();
  const { mutate: deleteExternalStorage, isLoading: deleteLoading } = apiHooks.useDelete();

  const existingStorageName = loadedData?.externalStorageName ?? "";

  const formValues = useMemo(
    () => buildDefaultFormValues(loadedData, defaultFacilityId, variant),
    [loadedData, defaultFacilityId, variant]
  );

  const form = useForm<ExternalStorageFormValues>({
    defaultValues: formValues,
    values: formValues
  });

  const {
    register,
    control,
    handleSubmit,
    getValues,
    setFocus,
    setError,
    clearErrors,
    watch,
    setValue,
    formState: { errors }
  } = form;

  const watchedDataType = watch("dataType");
  const watchedWarehouseName = watch("warehouseName");
  const isDefaultDataType = variantConfig.supportsDataType
    ? isSpecificDataType(watchedDataType)
    : true;

  const hasIncomingConnection = useMemo(
    () => chartEdges.some((edge: { target: string }) => edge.target === resolvedNodeId),
    [chartEdges, resolvedNodeId]
  );

  const isDisconnected = useMemo(() => {
    if (variant === "supplier") {
      return !hasIncomingConnection || !supplierIngredientId || !supplierId;
    }
    return !hasIncomingConnection || !ownerId || !sourceId;
  }, [
    hasIncomingConnection,
    ownerId,
    sourceId,
    supplierId,
    supplierIngredientId,
    variant
  ]);

  const isFormDisabled = lockStatus || isDisconnected;
  const saveDisabled =
    hasValidationErrors(errors) || lockStatus || isDisconnected || dropdownFacilitiesQuery.isLoading;

  const resolveStorageId = useCallback(
    (response: { _id?: string; id?: string; externalStorageId?: string } | null | undefined) => {
      const storageId = response?._id ?? response?.id ?? response?.externalStorageId;
      return storageId ? String(storageId) : "";
    },
    []
  );

  const loadStorageData = useCallback(async () => {
    if (!externalStorageId) {
      loadedStorageIdRef.current = null;
      loadedOriginalIdRef.current = null;
      setLoadedData(null);
      setOriginalData(null);
      return;
    }

    if (loadedStorageIdRef.current === externalStorageId) {
      return;
    }

    setIsLoadingData(true);
    try {
      const result = await refetchStorage?.();
      const storageData = result?.data ?? null;
      setLoadedData(storageData);
      loadedStorageIdRef.current = externalStorageId;
      loadedOriginalIdRef.current = null;
      setOriginalData(null);
    } finally {
      setIsLoadingData(false);
    }
  }, [externalStorageId, refetchStorage]);

  useEffect(() => {
    if (variant !== "simulate" || !simulateOriginalId) {
      return;
    }

    if (loadedOriginalIdRef.current === simulateOriginalId) {
      return;
    }

    loadedOriginalIdRef.current = simulateOriginalId;
    void refetchOriginal?.().then((result: { data?: any }) => {
      setOriginalData(result?.data ?? null);
    });
  }, [refetchOriginal, simulateOriginalId, variant]);

  useEffect(() => {
    if (newlyCreatedFacilityRef.current) {
      newlyCreatedFacilityRef.current = null;
      return;
    }

    if (externalStorageId) {
      void loadStorageData();
      return;
    }

    loadedStorageIdRef.current = null;
    loadedOriginalIdRef.current = null;
    setLoadedData(null);
    setOriginalData(null);
    if (defaultFacilityId) {
      setValue("warehouseName", defaultFacilityId);
    }
  }, [defaultFacilityId, externalStorageId, loadStorageData, setValue]);

  const updateDrawingNode = useCallback(
    async (updatedNode: any, successMessageText: string) => {
      try {
        const storageId = updatedNode?.data?.reference?.externalStorageId;
        if (variantConfig.supportsNotes && storageId) {
          await notesRef.current?.saveDocumentAndNote(storageId);
        }
        const updatedChartNodes = chartNodes.map((node: any) =>
          node.id === updatedNode?.id ? updatedNode : node
        );
        setChartNodes(updatedChartNodes);
        setNodeItem(updatedNode);
        saveDrawing(chartEdges, updatedChartNodes);
        successMessage(successMessageText);
      } catch (error) {
        errorView(error);
      }
    },
    [
      chartEdges,
      chartNodes,
      saveDrawing,
      setChartNodes,
      setNodeItem,
      variantConfig.supportsNotes
    ]
  );

  const onNodesDelete = useCallback(() => {
    if (variantConfig.supportsNotes) {
      notesRef.current?.deleteComment();
    }
    deleteElements({ nodes: [{ id: resolvedNodeId || "" }], edges: connectedEdges });
    setNodeItem(null);
  }, [connectedEdges, deleteElements, resolvedNodeId, setNodeItem, variantConfig.supportsNotes]);

  const handleFacilityChange = useCallback(
    (value: string) => {
      if (isNewFacilitySelection(value)) {
        setShowFacilityRegister(true);
        return;
      }
      setValue("warehouseName", value);
    },
    [setValue]
  );

  const handleFacilityModalClose = useCallback(
    (open: boolean, facilityData?: { newFacilityId?: string }) => {
      setShowFacilityRegister(open);
      if (facilityData?.newFacilityId) {
        newlyCreatedFacilityRef.current = facilityData.newFacilityId;
        void dropdownFacilitiesQuery.refetch?.().then(() => {
          setValue("warehouseName", facilityData.newFacilityId || "");
        });
      }
    },
    [dropdownFacilitiesQuery, setValue]
  );

  const onSubmit = useCallback(
    (values: ExternalStorageFormValues) => {
      const storageNameChanged = getValues("externalStorageName");

      if (
        existingStorageName &&
        existingStorageName !== storageNameChanged &&
        chartNodes &&
        storageNameChanged &&
        isNodeLabelExists(chartNodes as any, storageNameChanged)
      ) {
        clearErrors("externalStorageName");
        setFocus("externalStorageName");
        setError("externalStorageName", {
          type: "manual",
          message: NodeLabelExistError.EXTERNAL_WAREHOUSE
        });
        return;
      }

      const facilityId = values.warehouseName || defaultFacilityId || "";
      if (!facilityId) {
        errorView("Please select warehouse name");
        return;
      }

      if (variant === "supplier") {
        if (!supplierIngredientId || !supplierId) {
          errorView("Please connect ingredient before create external storage");
          return;
        }
      } else if (!ownerId || !sourceId) {
        errorView("Please connect ingredient before create external storage");
        return;
      }

      const payload = buildExternalStoragePayload({
        values,
        variant,
        ownerId,
        sourceId,
        supplierId,
        supplierIngredientId,
        facilityId,
        externalStorageId
      });

      const currentNode = (resolvedNodeId ? getNode(resolvedNodeId) : undefined) ?? nodeItem;
      const hasSavedReference = Boolean(
        currentNode?.data?.reference?.externalStorageId || externalStorageId
      );

      if (hasSavedReference) {
        const activeStorageId = String(
          currentNode?.data?.reference?.externalStorageId || externalStorageId || ""
        );

        setIsSubmitting(true);
        updateExternalStorage(payload, {
          onSuccess: (response: any) => {
            const storageId = resolveStorageId(response) || activeStorageId;
            setLoadedData(mapFormValuesToLoadedData(values, storageId, variant));
            loadedStorageIdRef.current = storageId;

            const updatedNode = {
              ...currentNode,
              data: {
                ...currentNode?.data,
                label: buildExternalStorageNodeLabel(
                  response?.externalStorageName || values.externalStorageName
                ),
                reference: {
                  ...currentNode?.data?.reference,
                  externalStorageId: storageId
                }
              }
            };

            void updateDrawingNode(updatedNode, "Ware house updated successfully").finally(() =>
              setIsSubmitting(false)
            );
          },
          onError: () => {
            setIsSubmitting(false);
          }
        });
        return;
      }

      setIsSubmitting(true);
      createExternalStorage(payload, {
        onSuccess: (response: any) => {
          const storageId = resolveStorageId(response);
          if (!storageId) {
            errorView("Failed to save external warehouse");
            setIsSubmitting(false);
            return;
          }

          setSavedExternalStorageId(storageId);
          setLoadedData(mapFormValuesToLoadedData(values, storageId, variant));
          loadedStorageIdRef.current = storageId;

          const updatedNode = {
            ...currentNode,
            data: {
              ...currentNode?.data,
              label: buildExternalStorageNodeLabel(
                response?.externalStorageName || values.externalStorageName
              ),
              reference: {
                externalStorageId: storageId
              }
            }
          };

          void updateDrawingNode(updatedNode, "External Warehouse created successfully").finally(
            () => setIsSubmitting(false)
          );
        },
        onError: () => {
          setIsSubmitting(false);
        }
      });
    },
    [
      chartNodes,
      clearErrors,
      createExternalStorage,
      defaultFacilityId,
      existingStorageName,
      externalStorageId,
      getNode,
      getValues,
      nodeItem,
      ownerId,
      resolveStorageId,
      resolvedNodeId,
      setError,
      setFocus,
      sourceId,
      supplierId,
      supplierIngredientId,
      updateDrawingNode,
      updateExternalStorage,
      variant
    ]
  );

  const handleDelete = useCallback(() => {
    if (externalStorageId) {
      deleteExternalStorage(externalStorageId, {
        onSuccess: () => {
          onNodesDelete();
          setVisibleDeleteModal(false);
          successMessage("External Warehouse deleted successfully");
        },
        onError: (error: any) => {
          errorView(error?.msg || "Failed to delete warehouse");
        }
      });
      return;
    }

    onNodesDelete();
    setVisibleDeleteModal(false);
    successMessage("External Warehouse deleted successfully");
  }, [deleteExternalStorage, externalStorageId, onNodesDelete]);

  const isInitialLoading =
    isLoadingData || (variant === "simulate" && Boolean(simulateOriginalId) && isOriginalLoading);
  const isSaving = isSubmitting || createLoading || updateLoading || deleteLoading;

  return {
    variant,
    variantConfig,
    isInitialLoading,
    isSaving,
    isFormDisabled,
    isDisconnected,
    saveDisabled,
    detailsExpanded,
    setDetailsExpanded,
    metricsExpanded,
    setMetricsExpanded,
    notesExpanded,
    setNotesExpanded,
    form,
    register,
    control,
    errors,
    handleSubmit,
    onSubmit,
    notesRef,
    externalStorageId,
    notesParentId: externalStorageId,
    facilities,
    searchFacilities,
    facilityLoading: dropdownFacilitiesQuery.isLoading || searchFacilitiesQuery.isLoading,
    handleFacilityChange,
    handleFacilityModalClose,
    showFacilityRegister,
    visibleFacilityManager,
    setVisibleFacilityManager,
    visibleFacilityModal,
    setVisibleFacilityModal,
    watchedWarehouseName,
    isDefaultDataType,
    originalData,
    visibleDeleteModal,
    setVisibleDeleteModal,
    handleDelete,
    lockStatus,
    refetchFacilityList: dropdownFacilitiesQuery.refetch,
    saveTitle: externalStorageId ? "Update" : "Submit",
    nameDisabled:
      variantConfig.lockNameWhenSaved && Boolean(externalStorageId)
  };
};
